import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'

import { InvoiceDetailsProps } from './PaymentCollectionAdd'

interface InvoiceGroupListProps {
  data: InvoiceDetailsProps[]
  isLoading: boolean
  setSelectedRowIds: React.Dispatch<React.SetStateAction<string[]>>
  setDiscountValues: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >
  selectedRowIds: string[]
  discountValues: Record<string, number>
}

// Memoized TextField component to prevent unnecessary re-renders
const DiscountTextField = React.memo(
  ({
    rowId,
    currentValue,
    onDiscountChange
  }: {
    rowId: string
    currentValue: number | string
    onDiscountChange: (rowId: string, value: number) => void
  }) => {
    return (
      <TextField
        type='number'
        size='small'
        variant='outlined'
        value={currentValue}
        onChange={(e) => {
          const value = parseFloat(e.target.value) || 0
          onDiscountChange(rowId, value)
        }}
        inputProps={{
          min: 0,
          step: 0.01
        }}
        sx={{
          width: '120px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'grey.300'
            }
          }
        }}
        placeholder='0.00'
      />
    )
  }
)

DiscountTextField.displayName = 'DiscountTextField'

export default function InvoiceDetailsList({
  data,
  isLoading,
  setSelectedRowIds,
  setDiscountValues,
  selectedRowIds,
  discountValues
}: InvoiceGroupListProps) {
  const [globalFilter, setGlobalFilter] = useState('')
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })

  // SOLUTION 1: Stable data transformation - don't include discount values in the base data
  const transformedData = useMemo(() => {
    return data.map((item, index) => {
      const uniqueId = item.order_id?.toString() || `row-${index}`
      return {
        ...item,
        id: uniqueId
        // Remove discount from here - handle it in the cell component instead
      }
    })
  }, [data]) // Only depend on data, not discountValues

  // Map rows by id for quick lookup (used by auto-allocation)
  const rowById = useMemo(() => {
    const map: Record<string, InvoiceDetailsProps & { id: string }> = {}
    transformedData.forEach((r) => (map[r.id] = r))
    return map
  }, [transformedData])

  // Compute auto amount: prefer pending_amount; fallback to (order_amount - paid_amount)
  const getAutoAllocation = useCallback((item: InvoiceDetailsProps) => {
    const pending = Number(item.pending_amount ?? 0)
    if (!Number.isNaN(pending) && pending > 0) return pending

    const order = Number(item.order_amount ?? 0)
    const paid = Number(item.paid_amount ?? 0)
    const calc = Math.max(order - paid, 0)

    return Number.isFinite(calc) ? calc : 0
  }, [])

  // SOLUTION 2: Stable callback handlers with useCallback
  const handleDiscountChange = useCallback(
    (rowId: string, value: number) => {
      setDiscountValues((prev) => ({
        ...prev,
        [rowId]: value
      }))
    },
    [setDiscountValues]
  )

  const handleRowSelection = useCallback(
    (rowId: string) => {
      setSelectedRowIds((prev) => {
        const isSelected = prev.includes(rowId)
        const nextSelected = isSelected
          ? prev.filter((id) => id !== rowId)
          : [...prev, rowId]

        // Auto-fill or clear discount value alongside selection
        setDiscountValues((prevVals) => {
          const nextVals = { ...prevVals }
          if (isSelected) {
            // unselect -> clear value
            delete nextVals[rowId]
          } else {
            // select -> prefill using pending (fallback to order - paid)
            const item = rowById[rowId]
            if (item) nextVals[rowId] = getAutoAllocation(item)
          }
          return nextVals
        })

        return nextSelected
      })
    },
    [setSelectedRowIds, setDiscountValues, rowById, getAutoAllocation]
  )

  const handleSelectAll = useCallback(() => {
    if (selectedRowIds.length === transformedData.length) {
      setSelectedRowIds([])
      // deselect all
      setDiscountValues((prevVals) => {
        const nextVals = { ...prevVals }
        transformedData.forEach((r) => delete nextVals[r.id])
        return nextVals
      })
    } else {
      setSelectedRowIds(transformedData.map((item) => item.id))
      // select all and prefill all
      setDiscountValues((prevVals) => {
        const nextVals = { ...prevVals }
        transformedData.forEach((item) => {
          nextVals[item.id] = getAutoAllocation(item)
        })
        return nextVals
      })
    }
  }, [
    selectedRowIds.length,
    transformedData,
    setSelectedRowIds,
    setDiscountValues,
    getAutoAllocation
  ])

  const [settleAmount, setSettleAmount] = useState<string>('')
  const handleAutoAllocatePartial = useCallback(() => {
    const parsed = parseFloat(settleAmount)

    // invalid or zero/negative → do nothing
    if (!Number.isFinite(parsed) || parsed <= 0) return

    const targetCents = Math.round(parsed * 100)
    let remainingCents = targetCents

    const nextSelected: string[] = []
    const nextDiscounts: Record<string, number> = {}

    for (const item of transformedData) {
      if (remainingCents <= 0) break

      const maxForRow = getAutoAllocation(item)
      if (!Number.isFinite(maxForRow) || maxForRow <= 0) continue

      const maxForRowCents = Math.round(maxForRow * 100)
      if (maxForRowCents <= 0) continue

      const allocateCents = Math.min(maxForRowCents, remainingCents)
      if (allocateCents <= 0) continue

      nextSelected.push(item.id)
      nextDiscounts[item.id] = allocateCents / 100

      remainingCents -= allocateCents
    }

    setSelectedRowIds(nextSelected)
    setDiscountValues(nextDiscounts)
  }, [
    settleAmount,
    transformedData,
    getAutoAllocation,
    setSelectedRowIds,
    setDiscountValues
  ])

  // SOLUTION 3: Move columns outside of component or make them more stable
  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={
              selectedRowIds.length === transformedData.length &&
              transformedData.length > 0
            }
            indeterminate={
              selectedRowIds.length > 0 &&
              selectedRowIds.length < transformedData.length
            }
            onChange={handleSelectAll}
          />
        ),
        cell: ({ row }) => {
          const rowId = row.original.id
          return (
            <Checkbox
              checked={selectedRowIds.includes(rowId)}
              onChange={() => handleRowSelection(rowId)}
            />
          )
        },
        size: 60,
        enableSorting: false
      },
      {
        accessorKey: 'order_id',
        header: 'Order ID',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 150
      },
      {
        accessorKey: 'product',
        header: 'Product',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 180
      },
      {
        accessorKey: 'currency',
        header: 'Currency',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 100
      },
      {
        accessorKey: 'package_name',
        header: 'Package Name',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 180
      },
      {
        accessorKey: 'order_amount',
        header: 'Order Paid',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'paid_amount',
        header: 'Already Paid',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 120
      },
      {
        accessorKey: 'pending_amount',
        header: 'Pending Amount',
        cell: ({ getValue }) => (
          <Typography variant='body2' noWrap>
            {getValue() as string}
          </Typography>
        ),
        size: 140
      },
      {
        id: 'discount',
        header: 'Amount to be allocated',
        cell: ({ row }) => {
          const rowId = row.original.id
          // Get the current value from discountValues, not from the row data
          const currentValue =
            discountValues[rowId] || row.original.discount || ''

          return (
            <DiscountTextField
              rowId={rowId}
              currentValue={currentValue}
              onDiscountChange={handleDiscountChange}
            />
          )
        },
        size: 150,
        enableSorting: false
      }
    ],
    [
      selectedRowIds,
      transformedData, // This is now stable
      handleRowSelection,
      handleDiscountChange,
      handleSelectAll,
      discountValues // Only include this for the discount cell
    ]
  )

  const table = useReactTable({
    data: transformedData,
    columns,
    state: {
      globalFilter,
      pagination
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
    debugTable: false
  })

  const totalFilteredPending = useMemo(() => {
    const rows = table.getFilteredRowModel().rows

    return rows.reduce((sum, row) => {
      const pending = Number(row.original.pending_amount ?? 0)
      return pending > 0 ? sum + pending : sum
    }, 0)
  }, [table.getState().globalFilter, data])

  const { rows } = table.getRowModel()
  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: isLoading ? 0 : rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 5
  })

  const virtualItems = isLoading ? [] : virtualizer.getVirtualItems()

  if (!transformedData.length && !isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '20px'
        }}
      >
        <Typography variant='body1' color='text.secondary'>
          No catalog groups available
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%', marginTop: '20px' }}>
      {/* Search Field */}
      {/* <Box sx={{ mb: 2 }}>
        <TextField
          placeholder='Search catalog groups...'
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          variant='outlined'
          size='small'
          sx={{ width: '300px' }}
        />
      </Box> */}

      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <Typography variant='body2' color='text.secondary'>
          Select rows manually or
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            flexWrap: 'wrap',
            justifyContent: 'flex-end'
          }}
        >
          <TextField
            label='Amount to settle'
            type='number'
            size='small'
            variant='outlined'
            value={settleAmount}
            onChange={(e) => {
              const raw = e.target.value
              const numeric = parseFloat(raw)

              // allow empty field
              if (raw === '') {
                setSettleAmount('')
                return
              }

              if (!Number.isFinite(numeric) || numeric < 0) {
                // ignore invalid values
                return
              }

              // clamp to totalFilteredPending
              if (numeric > totalFilteredPending) {
                setSettleAmount(totalFilteredPending.toFixed(2))
              } else {
                setSettleAmount(raw)
              }
            }}
            sx={{ width: '220px' }}
            inputProps={{
              min: 0,
              step: 0.01
            }}
            helperText={
              totalFilteredPending > 0
                ? `Max allocatable: ${totalFilteredPending.toFixed(2)}`
                : 'No pending amount to allocate'
            }
            error={
              !!settleAmount &&
              Number(settleAmount) > 0 &&
              Number(settleAmount) > totalFilteredPending
            }
          />
          <Button
            type='button'
            onClick={handleAutoAllocatePartial}
            sx={{
              m: 0
            }}
          >
            Auto allocate by amount
          </Button>
        </Box>
      </Box>

      {/* Table Container */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box
          ref={parentRef}
          sx={{
            height: '400px',
            overflowX: 'auto',
            overflowY: 'auto'
          }}
        >
          <Table sx={{ minWidth: '1200px' }}>
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        width: header.getSize(),
                        minWidth: header.getSize(),
                        backgroundColor: 'grey.50',
                        fontWeight: 'bold',
                        borderBottom: '2px solid',
                        borderColor: 'divider'
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            <TableBody
              sx={{
                position: 'relative',
                height:
                  isLoading || rows.length === 0
                    ? 'auto'
                    : `${virtualizer.getTotalSize()}px`
              }}
            >
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{
                      height: '200px',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}
                  >
                    <Stack alignItems='center' spacing={2}>
                      <CircularProgress />
                      <Typography variant='body2' color='text.secondary'>
                        Loading catalog groups...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    sx={{
                      height: '200px',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      No results found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index]
                    const rowId = row.original.id

                    return (
                      <TableRow
                        key={row.id}
                        style={{
                          position: 'absolute',
                          top: virtualRow.start,
                          left: 0,
                          width: '100%',
                          height: `${virtualRow.size}px`,
                          display: 'flex'
                        }}
                        sx={{
                          backgroundColor: selectedRowIds.includes(rowId)
                            ? 'action.selected'
                            : 'inherit',
                          '&:hover': {
                            backgroundColor: selectedRowIds.includes(rowId)
                              ? 'action.selected'
                              : 'action.hover'
                          }
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            sx={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                              maxWidth: cell.column.getSize(),
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              py: 1,
                              px: 2,
                              display: 'flex',
                              alignItems: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </>
              )}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Showing{' '}
            {table.getState().pagination.pageIndex *
              table.getState().pagination.pageSize +
              1}{' '}
            to{' '}
            {Math.min(
              (table.getState().pagination.pageIndex + 1) *
                table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} results
            {selectedRowIds.length > 0 &&
              ` • ${selectedRowIds.length} row${
                selectedRowIds.length === 1 ? '' : 's'
              } selected`}
          </Typography>

          <Pagination
            count={table.getPageCount()}
            page={table.getState().pagination.pageIndex + 1}
            onChange={(_, page) => table.setPageIndex(page - 1)}
            color='primary'
            size='small'
          />
        </Box>
      </Paper>
    </Box>
  )
}
